import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { query, where } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useSchool } from '../../contexts/SchoolContext';
import { useCollection } from '../../hooks/useFirestore';
import { detentionsCol } from '../../lib/firestore';
import { londonToday } from '../../lib/terms';
import type { Detention } from '../../types';

const MAX_SHOWN = 6;

/**
 * Who is in a Head Teacher detention today and has not been marked yet. The
 * after-school ones that morning lates and one-way breaches set are left to
 * the register's own tab — this card is named for Head Teacher detentions.
 */
export default function HomeDetentions() {
  const { appUser } = useAuth();
  const { activeModules, activeBehaviourFeatures, isDetentionStaff } = useSchool();
  const schoolId = appUser?.schoolId ?? '';
  const role = appUser?.role;

  const visible = !!schoolId
    && !!role && role !== 'superAdmin' && role !== 'student'
    && activeModules.includes('behaviour')
    && activeBehaviourFeatures.includes('detentions');

  const today = londonToday();

  // schoolId equality is required in the query itself — the rules on detentions
  // check resource.data.schoolId, so a listen without it is denied.
  const { data, loading, error } = useCollection<Detention>(
    visible ? query(detentionsCol, where('schoolId', '==', schoolId), where('date', '==', today)) : null,
    `${schoolId}:home-detentions:${today}`,
  );

  const rows = useMemo(() => data
    .filter((d) => d.type === 'headteacher' && d.status === 'scheduled')
    .sort((a, b) => a.studentName.localeCompare(b.studentName)), [data]);

  if (!visible) return null;

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-700">
          Head Teacher Detentions today{rows.length > 0 ? ` (${rows.length})` : ''}
        </h2>
        <Link to="/behaviour/detentions" className="shrink-0 text-xs font-medium text-brand-600 hover:underline">
          {isDetentionStaff ? 'Take the register →' : 'Book a pupil in →'}
        </Link>
      </div>

      {error ? (
        <p className="card p-4 text-sm text-amber-700">Couldn’t load today’s detentions — refresh to try again.</p>
      ) : loading ? (
        <p className="card p-4 text-sm text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="card p-4 text-sm text-gray-400">No Head Teacher detentions today.</p>
      ) : (
        <div className="card divide-y divide-gray-100 p-0">
          {rows.slice(0, MAX_SHOWN).map((d) => (
            <div key={d.id} className="px-4 py-2.5 text-sm">
              <span className="font-medium text-gray-800">{d.studentName}</span>
              {d.yearGroup && <span className="text-gray-400"> · Year {d.yearGroup}</span>}
              <span className="text-gray-400"> · {d.reason}</span>
            </div>
          ))}
          {rows.length > MAX_SHOWN && (
            <Link to="/behaviour/detentions" className="block px-4 py-2.5 text-xs font-medium text-brand-600 hover:underline">
              View all {rows.length} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
